/** Bumps a hex color's HSL lightness to at least `minL` (0–1), preserving hue + saturation. */
export function ensureMinLightness(hex: string, minL: number): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (l >= minL) return hex;
  const d = max - min;
  const s = d === 0 ? 0 : l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  const q = minL < 0.5 ? minL * (1 + s) : minL + s - minL * s;
  const p = 2 * minL - q;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const nr = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const ng = Math.round(hue2rgb(p, q, h) * 255);
  const nb = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
  return `#${nr.toString(16).padStart(2, "0")}${ng.toString(16).padStart(2, "0")}${nb.toString(16).padStart(2, "0")}`;
}

/**
 * For use as TEXT COLOR or SVG FILL on a dark page background.
 * Dark mode: bumps lightness to ≥62% so the color is always readable as text/fill.
 * Light mode: original color unchanged.
 */
export function teamColorFg(rawHex: string, isDark: boolean): string {
  return isDark ? ensureMinLightness(rawHex, 0.62) : rawHex;
}

/**
 * For use as BACKGROUND COLOR on chips or chart bars, with white text on top.
 * Dark mode: bumps lightness to ≥25% so the element is always visible against the dark page,
 * while staying dark enough that white text remains readable.
 * Light mode: original color unchanged.
 */
export function teamColorBg(rawHex: string, isDark: boolean): string {
  return isDark ? ensureMinLightness(rawHex, 0.25) : rawHex;
}
